import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Key, 
  Plus, 
  Trash2, 
  Copy, 
  RefreshCw, 
  Webhook,
  BarChart3,
  Code,
  Shield,
  Zap,
  CheckCircle,
  AlertTriangle,
  PlayCircle,
  FileText,
  CreditCard,
  TrendingUp,
  Coins,
  Crown,
  Rocket,
  Building2,
  ArrowUp
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

const APIManagementPage = () => {
  const [activeTab, setActiveTab] = useState("keys");
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Billing state
  const [billingInfo, setBillingInfo] = useState(null);
  const [plans, setPlans] = useState({});
  const [creditCosts, setCreditCosts] = useState({});
  const [addCreditsDialogOpen, setAddCreditsDialogOpen] = useState(false);
  const [creditsToAdd, setCreditsToAdd] = useState(100);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // Dialogs
  const [keyDialogOpen, setKeyDialogOpen] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  
  // Forms
  const [keyForm, setKeyForm] = useState({
    name: "",
    description: "",
    rate_limit: 1000,
    allowed_apis: ["fastag", "rc", "eway", "gst"]
  });
  
  const [webhookForm, setWebhookForm] = useState({
    name: "",
    url: "",
    events: []
  });

  const availableApis = [
    { id: "fastag", name: "FASTag Tracking", description: "Track toll transactions", credits: 1 },
    { id: "rc", name: "RC Verification", description: "Verify vehicle registration", credits: 2 },
    { id: "eway", name: "E-Way Bill", description: "Verify e-way bills", credits: 2 },
    { id: "gst", name: "GST API", description: "GST verification (coming soon)", credits: 3 }
  ];

  const availableEvents = [
    { id: "fastag.tracked", name: "FASTag Tracked", description: "When a vehicle is tracked" },
    { id: "rc.verified", name: "RC Verified", description: "When RC is verified" },
    { id: "eway.verified", name: "E-Way Bill Verified", description: "When E-Way Bill is verified" },
    { id: "test.ping", name: "Test Ping", description: "Test webhook" }
  ];

  const planIcons = {
    free: Zap,
    starter: Rocket,
    professional: Crown,
    enterprise: Building2
  };

  const planColors = {
    free: "from-gray-600 to-gray-700",
    starter: "from-blue-600 to-blue-700",
    professional: "from-purple-600 to-purple-700",
    enterprise: "from-amber-600 to-amber-700"
  };

  useEffect(() => {
    fetchApiKeys();
    fetchWebhooks();
    fetchUsageStats();
    fetchBillingInfo();
    fetchPlans();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API_URL}/developer/keys`);
      setApiKeys(response.data);
    } catch (error) {
      console.error("Error fetching API keys:", error);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const response = await axios.get(`${API_URL}/developer/webhooks`);
      setWebhooks(response.data);
    } catch (error) {
      console.error("Error fetching webhooks:", error);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/developer/usage`);
      setUsageStats(response.data);
    } catch (error) {
      console.error("Error fetching usage:", error);
    }
  };

  const fetchBillingInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/developer/billing`);
      setBillingInfo(response.data);
    } catch (error) {
      console.error("Error fetching billing:", error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_URL}/developer/plans`);
      setPlans(response.data.plans || {});
      setCreditCosts(response.data.credit_costs || {});
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const handleAddCredits = async () => {
    if (creditsToAdd <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/developer/billing/add-credits`, null, {
        params: { credits: creditsToAdd, payment_method: "manual" }
      });
      toast.success(response.data.message);
      setAddCreditsDialogOpen(false);
      fetchBillingInfo();
    } catch (error) {
      console.error("Error adding credits:", error);
      toast.error("Failed to add credits");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradePlan = async (planId) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/developer/billing/upgrade`, null, {
        params: { plan: planId }
      });
      toast.success(response.data.message);
      setUpgradeDialogOpen(false);
      fetchBillingInfo();
    } catch (error) {
      console.error("Error upgrading plan:", error);
      toast.error("Failed to upgrade plan");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!keyForm.name) {
      toast.error("Please enter a key name");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/developer/keys`, keyForm);
      setNewApiKey(response.data.api_key);
      setNewKeyDialogOpen(true);
      setKeyDialogOpen(false);
      setKeyForm({ name: "", description: "", rate_limit: 1000, allowed_apis: ["fastag", "rc", "eway", "gst"] });
      fetchApiKeys();
      toast.success("API key created successfully!");
    } catch (error) {
      console.error("Error creating key:", error);
      toast.error("Failed to create API key");
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to revoke this API key? This action cannot be undone.")) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/developer/keys/${keyId}`);
      toast.success("API key revoked");
      fetchApiKeys();
    } catch (error) {
      console.error("Error revoking key:", error);
      toast.error("Failed to revoke API key");
    }
  };

  const handleCreateWebhook = async (e) => {
    e.preventDefault();
    if (!webhookForm.name || !webhookForm.url || webhookForm.events.length === 0) {
      toast.error("Please fill all required fields");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/developer/webhooks`, webhookForm);
      setWebhookDialogOpen(false);
      setWebhookForm({ name: "", url: "", events: [] });
      fetchWebhooks();
      toast.success("Webhook created successfully!");
    } catch (error) {
      console.error("Error creating webhook:", error);
      toast.error("Failed to create webhook");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm("Delete this webhook?")) return;
    
    try {
      await axios.delete(`${API_URL}/developer/webhooks/${webhookId}`);
      toast.success("Webhook deleted");
      fetchWebhooks();
    } catch (error) {
      console.error("Error deleting webhook:", error);
      toast.error("Failed to delete webhook");
    }
  };

  const handleTestWebhook = async (webhookId) => {
    try {
      toast.loading("Testing webhook...", { id: "webhook-test" });
      const response = await axios.post(`${API_URL}/developer/webhooks/${webhookId}/test`);
      if (response.data.success) {
        toast.success("Webhook test successful!", { id: "webhook-test" });
      } else {
        toast.error(`Webhook test failed: ${response.data.error || response.data.status_code}`, { id: "webhook-test" });
      }
    } catch (error) {
      toast.error("Webhook test failed", { id: "webhook-test" });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const toggleApiAccess = (apiId) => {
    const current = keyForm.allowed_apis;
    if (current.includes(apiId)) {
      setKeyForm({ ...keyForm, allowed_apis: current.filter(a => a !== apiId) });
    } else {
      setKeyForm({ ...keyForm, allowed_apis: [...current, apiId] });
    }
  };

  const toggleWebhookEvent = (eventId) => {
    const current = webhookForm.events;
    if (current.includes(eventId)) {
      setWebhookForm({ ...webhookForm, events: current.filter(e => e !== eventId) });
    } else {
      setWebhookForm({ ...webhookForm, events: [...current, eventId] });
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Code className="w-8 h-8 text-primary" />
            API Platform
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your API keys, billing, and monitor usage
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setAddCreditsDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Credits
          </Button>
          <Button onClick={() => setUpgradeDialogOpen(true)}>
            <ArrowUp className="w-4 h-4 mr-2" />
            Upgrade Plan
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Credits Balance Card */}
        <Card className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/30 border-emerald-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Credits Balance</p>
                <p className="text-2xl font-bold text-emerald-400">
                  {billingInfo?.billing?.credits_balance || 0}
                </p>
                <p className="text-xs text-muted-foreground">
                  {billingInfo?.plan_details?.name || "Free"} Plan
                </p>
              </div>
              <Coins className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/30 border-blue-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active API Keys</p>
                <p className="text-2xl font-bold">{apiKeys.filter(k => k.status === "active").length}</p>
              </div>
              <Key className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-900/30 to-green-800/30 border-green-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total API Calls</p>
                <p className="text-2xl font-bold">{usageStats?.total_requests || 0}</p>
              </div>
              <Zap className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-900/30 to-purple-800/30 border-purple-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Webhooks</p>
                <p className="text-2xl font-bold">{webhooks.filter(w => w.status === "active").length}</p>
              </div>
              <Webhook className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-900/30 to-amber-800/30 border-amber-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Rate Limit</p>
                <p className="text-2xl font-bold">{billingInfo?.plan_details?.rate_limit_per_day || 100}/day</p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="billing" data-testid="tab-billing">
            <CreditCard className="w-4 h-4 mr-2" />
            Billing & Plans
          </TabsTrigger>
          <TabsTrigger value="keys" data-testid="tab-api-keys">
            <Key className="w-4 h-4 mr-2" />
            API Keys
          </TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="tab-webhooks">
            <Webhook className="w-4 h-4 mr-2" />
            Webhooks
          </TabsTrigger>
          <TabsTrigger value="usage" data-testid="tab-usage">
            <BarChart3 className="w-4 h-4 mr-2" />
            Usage
          </TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">
            <FileText className="w-4 h-4 mr-2" />
            Docs
          </TabsTrigger>
        </TabsList>

        {/* Billing & Plans Tab */}
        <TabsContent value="billing" className="space-y-4">
          {/* Current Plan Card */}
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-primary" />
                Current Plan: {billingInfo?.plan_details?.name || "Free"}
              </CardTitle>
              <CardDescription>
                {billingInfo?.plan_details?.price === 0 ? "Free forever" : formatCurrency(billingInfo?.plan_details?.price || 0) + "/month"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-secondary/30 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Credits Balance</p>
                  <p className="text-3xl font-bold text-emerald-400">{billingInfo?.billing?.credits_balance || 0}</p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Used This Month</p>
                  <p className="text-3xl font-bold">{billingInfo?.billing?.credits_used_this_month || 0}</p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Monthly Allocation</p>
                  <p className="text-3xl font-bold">{billingInfo?.plan_details?.credits_per_month || 100}</p>
                </div>
                <div className="bg-secondary/30 p-4 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">Rate Limit</p>
                  <p className="text-3xl font-bold">{billingInfo?.plan_details?.rate_limit_per_day || 100}/day</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pricing Plans */}
          <Card>
            <CardHeader>
              <CardTitle>Available Plans</CardTitle>
              <CardDescription>Choose a plan that fits your needs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                {Object.entries(plans).map(([planId, plan]) => {
                  const PlanIcon = planIcons[planId] || Zap;
                  const isCurrentPlan = billingInfo?.billing?.plan === planId;
                  
                  return (
                    <div 
                      key={planId}
                      className={`relative p-6 rounded-lg border-2 transition-all ${
                        isCurrentPlan 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {isCurrentPlan && (
                        <Badge className="absolute -top-2 right-4 bg-primary">Current</Badge>
                      )}
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${planColors[planId] || planColors.free} flex items-center justify-center mb-4`}>
                        <PlanIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                      <p className="text-2xl font-bold text-primary mb-4">
                        {plan.price === 0 ? "Free" : formatCurrency(plan.price)}
                        {plan.price > 0 && <span className="text-sm font-normal text-muted-foreground">/month</span>}
                      </p>
                      <ul className="space-y-2 mb-6 text-sm">
                        <li className="flex items-center gap-2">
                          <Coins className="w-4 h-4 text-emerald-400" />
                          {plan.credits_per_month.toLocaleString()} credits/month
                        </li>
                        <li className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-blue-400" />
                          {plan.rate_limit_per_day.toLocaleString()} requests/day
                        </li>
                        {plan.features?.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Button 
                        className="w-full"
                        variant={isCurrentPlan ? "outline" : "default"}
                        disabled={isCurrentPlan}
                        onClick={() => handleUpgradePlan(planId)}
                      >
                        {isCurrentPlan ? "Current Plan" : planId === "free" ? "Downgrade" : "Upgrade"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Credit Costs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Coins className="w-5 h-5" />
                API Credit Costs
              </CardTitle>
              <CardDescription>Credits are deducted per successful API call</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                {availableApis.map(api => (
                  <div key={api.id} className="bg-secondary/30 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{api.name}</span>
                      <Badge variant="outline">{creditCosts[api.id] || api.credits} credit{(creditCosts[api.id] || api.credits) > 1 ? 's' : ''}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{api.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Credit Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Time</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!billingInfo?.recent_transactions || billingInfo.recent_transactions.length === 0) ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                        No transactions yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    billingInfo.recent_transactions.map((tx, idx) => (
                      <TableRow key={idx} className="border-border">
                        <TableCell className="text-sm">
                          {new Date(tx.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={tx.amount > 0 ? "text-green-400" : "text-red-400"}>
                            {tx.type}
                          </Badge>
                        </TableCell>
                        <TableCell>{tx.description}</TableCell>
                        <TableCell className={`text-right font-mono font-bold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {tx.amount > 0 ? '+' : ''}{tx.amount}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Keys Tab */}
        <TabsContent value="keys" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>API Keys</CardTitle>
                <CardDescription>Manage your API keys for accessing the platform APIs</CardDescription>
              </div>
              <Button onClick={() => setKeyDialogOpen(true)} data-testid="create-api-key-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create API Key
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>API Key</TableHead>
                    <TableHead>Rate Limit</TableHead>
                    <TableHead>APIs</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Used</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiKeys.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        <Key className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No API keys created yet</p>
                        <p className="text-sm">Create your first API key to get started</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    apiKeys.map((key) => (
                      <TableRow key={key.id} className="border-border">
                        <TableCell>
                          <div>
                            <p className="font-medium">{key.name}</p>
                            {key.description && (
                              <p className="text-xs text-muted-foreground">{key.description}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-secondary/50 px-2 py-1 rounded">
                            {key.api_key_masked}
                          </code>
                        </TableCell>
                        <TableCell>{key.rate_limit}/day</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {key.allowed_apis?.map(api => (
                              <Badge key={api} variant="outline" className="text-xs">
                                {api}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{key.usage_count || 0}</TableCell>
                        <TableCell>
                          <Badge className={key.status === "active" ? "bg-green-500" : "bg-red-500"}>
                            {key.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {key.last_used ? new Date(key.last_used).toLocaleDateString() : "Never"}
                        </TableCell>
                        <TableCell>
                          {key.status === "active" && (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-400"
                              onClick={() => handleRevokeKey(key.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Webhooks Tab */}
        <TabsContent value="webhooks" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Webhooks</CardTitle>
                <CardDescription>Configure webhooks to receive real-time notifications</CardDescription>
              </div>
              <Button onClick={() => setWebhookDialogOpen(true)} data-testid="create-webhook-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Webhook
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Success/Fail</TableHead>
                    <TableHead>Last Triggered</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        <Webhook className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No webhooks configured</p>
                        <p className="text-sm">Create a webhook to receive real-time notifications</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    webhooks.map((webhook) => (
                      <TableRow key={webhook.id} className="border-border">
                        <TableCell className="font-medium">{webhook.name}</TableCell>
                        <TableCell>
                          <code className="text-xs bg-secondary/50 px-2 py-1 rounded truncate max-w-[200px] block">
                            {webhook.url}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {webhook.events?.map(event => (
                              <Badge key={event} variant="outline" className="text-xs">
                                {event}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={webhook.status === "active" ? "bg-green-500" : "bg-gray-500"}>
                            {webhook.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-green-400">{webhook.success_count || 0}</span>
                          {" / "}
                          <span className="text-red-400">{webhook.failure_count || 0}</span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {webhook.last_triggered ? new Date(webhook.last_triggered).toLocaleString() : "Never"}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleTestWebhook(webhook.id)}
                              title="Test Webhook"
                            >
                              <PlayCircle className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-400"
                              onClick={() => handleDeleteWebhook(webhook.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Usage Tab */}
        <TabsContent value="usage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                API Usage Statistics
              </CardTitle>
              <CardDescription>Monitor your API usage and performance</CardDescription>
            </CardHeader>
            <CardContent>
              {usageStats ? (
                <div className="space-y-6">
                  {/* Endpoint Stats */}
                  <div>
                    <h4 className="font-medium mb-3">Usage by Endpoint</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {usageStats.endpoint_stats?.map((stat, idx) => (
                        <div key={idx} className="bg-secondary/30 p-4 rounded-lg">
                          <p className="text-sm text-muted-foreground">{stat._id}</p>
                          <p className="text-2xl font-bold">{stat.total_calls}</p>
                          <p className="text-xs text-muted-foreground">
                            Avg: {Math.round(stat.avg_response_time)}ms
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Recent Logs */}
                  <div>
                    <h4 className="font-medium mb-3">Recent API Calls</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Endpoint</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Response Time</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usageStats.recent_logs?.slice(0, 10).map((log, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm">
                              {new Date(log.timestamp).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <code className="text-xs">{log.endpoint}</code>
                            </TableCell>
                            <TableCell>
                              <Badge className={log.response_status === 200 ? "bg-green-500" : "bg-red-500"}>
                                {log.response_status}
                              </Badge>
                            </TableCell>
                            <TableCell>{log.response_time_ms}ms</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No usage data yet</p>
                  <p className="text-sm">Make some API calls to see analytics</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documentation Tab */}
        <TabsContent value="docs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                API Documentation
              </CardTitle>
              <CardDescription>Quick reference for using the Ardhanageshwar Logistics API</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Base URL */}
              <div className="bg-secondary/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Base URL</h4>
                <code className="bg-secondary px-3 py-2 rounded block">
                  {window.location.origin}/api/v1
                </code>
              </div>

              {/* Authentication */}
              <div className="bg-secondary/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Authentication</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Pass your API key as a query parameter:
                </p>
                <code className="bg-secondary px-3 py-2 rounded block text-sm">
                  ?api_key=YOUR_API_KEY
                </code>
              </div>

              {/* Endpoints */}
              <div className="space-y-4">
                <h4 className="font-medium">Available Endpoints</h4>
                
                {/* FASTag */}
                <div className="bg-secondary/20 p-4 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-blue-500">POST</Badge>
                    <code className="text-sm">/fastag/track</code>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">Track vehicle toll history via FASTag</p>
                  <div className="bg-secondary/50 p-3 rounded text-xs font-mono">
                    <p className="text-muted-foreground"># Example Request</p>
                    <p>curl -X POST &quot;{window.location.origin}/api/v1/fastag/track?vehicle_number=HR55AT4106&amp;api_key=YOUR_KEY&quot;</p>
                  </div>
                </div>

                {/* RC */}
                <div className="bg-secondary/20 p-4 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-green-500">POST</Badge>
                    <code className="text-sm">/rc/verify</code>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">Verify vehicle registration certificate</p>
                  <div className="bg-secondary/50 p-3 rounded text-xs font-mono">
                    <p className="text-muted-foreground"># Example Request</p>
                    <p>curl -X POST &quot;{window.location.origin}/api/v1/rc/verify?vehicle_number=HR55AT4106&amp;api_key=YOUR_KEY&quot;</p>
                  </div>
                </div>

                {/* E-Way Bill */}
                <div className="bg-secondary/20 p-4 rounded-lg border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-orange-500">POST</Badge>
                    <code className="text-sm">/eway/verify</code>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">Verify E-Way Bill details</p>
                  <div className="bg-secondary/50 p-3 rounded text-xs font-mono">
                    <p className="text-muted-foreground"># Example Request</p>
                    <p>curl -X POST &quot;{window.location.origin}/api/v1/eway/verify?ewb_number=741625836705&amp;api_key=YOUR_KEY&quot;</p>
                  </div>
                </div>
              </div>

              {/* Rate Limits */}
              <div className="bg-secondary/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Rate Limits</h4>
                <p className="text-sm text-muted-foreground">
                  Default rate limit is 1,000 requests per day. Contact support for higher limits.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Rate limit exceeded responses return HTTP 429.
                </p>
              </div>

              {/* Webhooks */}
              <div className="bg-secondary/30 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Webhooks</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  Configure webhooks to receive real-time notifications:
                </p>
                <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                  <li><code>fastag.tracked</code> - Vehicle toll data retrieved</li>
                  <li><code>rc.verified</code> - RC verification completed</li>
                  <li><code>eway.verified</code> - E-Way Bill verified</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create API Key Dialog */}
      <Dialog open={keyDialogOpen} onOpenChange={setKeyDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-400" />
              Create API Key
            </DialogTitle>
            <DialogDescription>Create a new API key to access the platform APIs</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateKey} className="space-y-4">
            <div>
              <Label>Key Name *</Label>
              <Input
                placeholder="e.g., Production Key"
                value={keyForm.name}
                onChange={(e) => setKeyForm({ ...keyForm, name: e.target.value })}
                className="bg-secondary/50"
                data-testid="api-key-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Input
                placeholder="What is this key for?"
                value={keyForm.description}
                onChange={(e) => setKeyForm({ ...keyForm, description: e.target.value })}
                className="bg-secondary/50"
              />
            </div>
            <div>
              <Label>Rate Limit (requests/day)</Label>
              <Input
                type="number"
                value={keyForm.rate_limit}
                onChange={(e) => setKeyForm({ ...keyForm, rate_limit: parseInt(e.target.value) })}
                className="bg-secondary/50"
              />
            </div>
            <div>
              <Label>API Access</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {availableApis.map(api => (
                  <div 
                    key={api.id}
                    onClick={() => toggleApiAccess(api.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      keyForm.allowed_apis.includes(api.id) 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border bg-secondary/30'
                    }`}
                  >
                    <p className="font-medium text-sm">{api.name}</p>
                    <p className="text-xs text-muted-foreground">{api.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setKeyDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Create Key"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New API Key Display Dialog */}
      <Dialog open={newKeyDialogOpen} onOpenChange={setNewKeyDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              API Key Created!
            </DialogTitle>
            <DialogDescription>
              Copy your API key now. You will not be able to see it again!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-secondary/50 p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <code className="text-sm break-all">{newApiKey}</code>
                <Button size="sm" variant="ghost" onClick={() => copyToClipboard(newApiKey)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400 mt-0.5" />
                <p className="text-sm text-yellow-400">
                  Save this key securely. For security reasons, we cannot show it again.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewKeyDialogOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Webhook Dialog */}
      <Dialog open={webhookDialogOpen} onOpenChange={setWebhookDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Webhook className="w-5 h-5 text-purple-400" />
              Create Webhook
            </DialogTitle>
            <DialogDescription>Configure a webhook to receive real-time notifications</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateWebhook} className="space-y-4">
            <div>
              <Label>Webhook Name *</Label>
              <Input
                placeholder="e.g., Order Notifications"
                value={webhookForm.name}
                onChange={(e) => setWebhookForm({ ...webhookForm, name: e.target.value })}
                className="bg-secondary/50"
                data-testid="webhook-name-input"
              />
            </div>
            <div>
              <Label>Endpoint URL *</Label>
              <Input
                type="url"
                placeholder="https://your-server.com/webhook"
                value={webhookForm.url}
                onChange={(e) => setWebhookForm({ ...webhookForm, url: e.target.value })}
                className="bg-secondary/50"
                data-testid="webhook-url-input"
              />
            </div>
            <div>
              <Label>Events to Subscribe</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {availableEvents.map(event => (
                  <div 
                    key={event.id}
                    onClick={() => toggleWebhookEvent(event.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      webhookForm.events.includes(event.id) 
                        ? 'border-primary bg-primary/10' 
                        : 'border-border bg-secondary/30'
                    }`}
                  >
                    <p className="font-medium text-sm">{event.name}</p>
                    <p className="text-xs text-muted-foreground">{event.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWebhookDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Create Webhook"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Credits Dialog */}
      <Dialog open={addCreditsDialogOpen} onOpenChange={setAddCreditsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-emerald-400" />
              Add Credits
            </DialogTitle>
            <DialogDescription>
              Add credits to your API balance. Current balance: {billingInfo?.billing?.credits_balance || 0}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Credits Amount</Label>
              <Input
                type="number"
                min={1}
                value={creditsToAdd}
                onChange={(e) => setCreditsToAdd(parseInt(e.target.value) || 0)}
                className="bg-secondary/50"
              />
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[100, 500, 1000, 5000].map(amount => (
                <Button 
                  key={amount}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCreditsToAdd(amount)}
                  className={creditsToAdd === amount ? 'border-primary' : ''}
                >
                  {amount}
                </Button>
              ))}
            </div>
            <div className="bg-secondary/30 p-3 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>New Balance</span>
                <span className="font-bold text-emerald-400">
                  {(billingInfo?.billing?.credits_balance || 0) + creditsToAdd}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setAddCreditsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddCredits} disabled={loading || creditsToAdd <= 0}>
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : `Add ${creditsToAdd} Credits`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Plan Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-primary" />
              Upgrade Your Plan
            </DialogTitle>
            <DialogDescription>
              Choose a plan to unlock more credits and higher rate limits
            </DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {Object.entries(plans).map(([planId, plan]) => {
              const PlanIcon = planIcons[planId] || Zap;
              const isCurrentPlan = billingInfo?.billing?.plan === planId;
              
              return (
                <div 
                  key={planId}
                  className={`relative p-5 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedPlan === planId 
                      ? 'border-primary bg-primary/10' 
                      : isCurrentPlan
                      ? 'border-green-500/50 bg-green-500/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => !isCurrentPlan && setSelectedPlan(planId)}
                >
                  {isCurrentPlan && (
                    <Badge className="absolute -top-2 right-4 bg-green-500">Current</Badge>
                  )}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${planColors[planId] || planColors.free} flex items-center justify-center`}>
                      <PlanIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold">{plan.name}</h3>
                      <p className="text-lg font-bold text-primary">
                        {plan.price === 0 ? "Free" : formatCurrency(plan.price)}<span className="text-xs font-normal">/mo</span>
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>{plan.credits_per_month.toLocaleString()} credits/month</p>
                    <p>{plan.rate_limit_per_day.toLocaleString()} requests/day</p>
                    <p>{plan.support} Support</p>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => setUpgradeDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={() => selectedPlan && handleUpgradePlan(selectedPlan)} 
              disabled={loading || !selectedPlan || billingInfo?.billing?.plan === selectedPlan}
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Upgrade Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default APIManagementPage;
